import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Group,
  SimpleGrid,
  Card,
  Image,
  Badge,
  Text,
  Modal,
  Stack,
  Avatar,
  Tooltip,
  LoadingOverlay,
} from "@mantine/core";
import { IconPlus, IconLink, IconEdit, IconLock } from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

const BASE_URL = `${API_BASE_URL}/shop-owner`;
const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("shopToken")}`,
  "Content-Type": "application/json",
});
export default function ShopInventoryList({ shopId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);

  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const currentUser = JSON.parse(localStorage.getItem("shopUser") || "{}");

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/shops/${shopId}/products`, {
        headers: getHeaders(),
      });
      const data = await res.json();
      if (data.success) setProducts(data.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [shopId]);

  const openLinkModal = async () => {
    const res = await fetch(`${BASE_URL}/products/available`, {
      headers: getHeaders(),
    });
    const data = await res.json();
    if (data.success) {
      setAvailableProducts(data.data);
      setLinkModalOpen(true);
    }
  };

  const handleLink = async (productId, action) => {
    const res = await fetch(`${BASE_URL}/products/link`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ shopId, productId, action }),
    });
    if (res.ok) {
      showSuccess("Success", "Inventory updated");
      setLinkModalOpen(false);
      loadData();
    }
  };

  return (
    <Stack>
      <Group justify="flex-end">
        <Button
          variant="default"
          leftSection={<IconLink size={16} />}
          onClick={openLinkModal}
        >
          Link Existing
        </Button>
        <Button
          className="btn-ota-primary"
          leftSection={<IconPlus size={16} />}
          onClick={() => navigate(`/shop-owner/shops/${shopId}/product/new`)}
        >
          Create Product
        </Button>
      </Group>

      <div style={{ position: "relative", minHeight: 200 }}>
        <LoadingOverlay visible={loading} />
        {products.length === 0 && !loading ? (
          <Text c="dimmed" ta="center" py="xl">
            No products in this shop yet.
          </Text>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {products.map((p) => {
              const isOwner =
                p.creatorId === currentUser.id ||
                p.creatorId === currentUser.cuid;
              return (
                <Card key={p.id} withBorder radius="md" padding="sm">
                  <Card.Section>
                    <Image
                      src={p.media[0]?.url}
                      h={140}
                      fit="cover"
                      fallbackSrc="https://placehold.co/400"
                    />
                  </Card.Section>
                  <Group justify="space-between" mt="md" mb="xs">
                    <Text fw={500} lineClamp={1}>
                      {p.title}
                    </Text>
                    <Badge color={isOwner ? "blue" : "orange"} variant="light">
                      {isOwner ? "Yours" : "Linked"}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Price: ₹{p.unitPrice}
                  </Text>

                  <Group mt="md" grow>
                    {isOwner ? (
                      <Button
                        variant="light"
                        size="xs"
                        leftSection={<IconEdit size={14} />}
                        onClick={() =>
                          navigate(
                            `/shop-owner/shops/${shopId}/product/edit/${p.id}`,
                          )
                        }
                      >
                        Edit
                      </Button>
                    ) : (
                      <Tooltip label="Cannot edit products owned by others">
                        <Button
                          disabled
                          variant="subtle"
                          size="xs"
                          leftSection={<IconLock size={14} />}
                        >
                          Locked
                        </Button>
                      </Tooltip>
                    )}
                    <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      onClick={() => handleLink(p.id, "UNLINK")}
                    >
                      Unlink
                    </Button>
                  </Group>
                </Card>
              );
            })}
          </SimpleGrid>
        )}
      </div>

      <Modal
        opened={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        title="Link Available Products"
        size="lg"
      >
        <Stack>
          {availableProducts.length === 0 ? (
            <Text c="dimmed" ta="center">
              No products available.
            </Text>
          ) : (
            availableProducts.map((ap) => (
              <Group
                key={ap.id}
                justify="space-between"
                p="sm"
                bg="gray.0"
                style={{ borderRadius: 8 }}
              >
                <Group>
                  <Avatar src={ap.media[0]?.url} radius="sm" />
                  <div>
                    <Text size="sm" fw={500}>
                      {ap.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      ₹{ap.unitPrice}
                    </Text>
                  </div>
                </Group>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => handleLink(ap.id, "LINK")}
                >
                  Link
                </Button>
              </Group>
            ))
          )}
        </Stack>
      </Modal>
    </Stack>
  );
}
